import { EVENT_SETTABLE_COLUMN_IDS } from '@features/event-level-column-rename/constants';
import type {
  EventSettableColumnId,
  LineItemColumnLabelsPayload,
} from '@features/event-level-column-rename/types';

/**
 * Turns the rename form's changed values into the write endpoint's body.
 *
 * The body is sparse by design: it carries only the columns passed in, which the drawer limits
 * to the fields the buyer actually edited. The endpoint merges rather than replaces — an id
 * absent from the payload leaves the stored event layer untouched — so a value that merely
 * arrived as an org-level prefill is never written back as an event override, which would
 * silently freeze that column against future org-wide renames.
 *
 * Trimming happens here because the producer strips, and an edited-then-emptied input becomes
 * an explicit `null` — the only signal that clears an event override and lets the org setting
 * retake priority for that column.
 */
export const buildLabelPayload = (
  values: Partial<Record<EventSettableColumnId, string>>
): LineItemColumnLabelsPayload => ({
  line_item_column_labels: EVENT_SETTABLE_COLUMN_IDS.reduce((payload, columnId) => {
    if (!(columnId in values)) return payload;

    const trimmed = (values[columnId] ?? '').trim();

    return { ...payload, [columnId]: trimmed === '' ? null : trimmed };
  }, {} as LineItemColumnLabelsPayload['line_item_column_labels']),
});
